import React, {useState} from "react";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import type {PickerRangeValue} from "@mui/x-date-pickers/internals";
import type {Dayjs} from "dayjs";
import dayjs from "dayjs";
import {Button, Grid, Popover, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers-pro";
import {Clear, ExpandMore} from "@mui/icons-material";
import type {DayJsObject} from "../../utils/day-js-utils.ts";

declare type Props = {
    /** Current value */
    value: PickerRangeValue
    /** Click handler for applying the updates from DateRangePicker. */
    onChange: (value: PickerRangeValue) => void,
    /** Do not allow to open of popover */
    disabled?: boolean
}

/** The DateRangePicker component allows a user to select a range of dates */
export const QuarterDateRangePickerMui: React.FC<Props> = ({value, onChange, disabled}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
        setIsOpen(true)
    };
    const handleClose = () => {
        setAnchorEl(null)
        setIsOpen(false)
    }

    const [isOpen, setIsOpen] = useState(false)

    const [range, setRange] = useState<PickerRangeValue>([...value])


    //Quarter is 0 indexed
    const onClickQuarter: (isTo: boolean, quarter: number) => void = (isTo, quarter) => {
        let date = isTo ? range[1] : range[0];

        console.log('date w/o quarter', date)

        if (date) {
            date = date.month(quarter * 3); //- 3
            console.log('date w quarter', date.format('YYYY-MM-DD'))

            if (isTo) {
                setRange(r => [r[0], date])
            } else {
                setRange(r => [date, r[1]])
            }
        }
    }

    const showDateQuarter = (date: Dayjs) => {
        return date.format('YYYY-') + 'Q' + (Math.floor(date.month() / 3) + 1)
    }

    const getLabel = () => {
        if (range[0] && range[1]) {
            return `${showDateQuarter(range[0])} - ${showDateQuarter(range[1])}`
        } else if (range[0]) {
            return `${showDateQuarter(range[0])} onwards`
        } else if (range[1]) {
            return `To ${showDateQuarter(range[1])}`
        } else if (range[0] == null && range[1] == null) {
            return 'All dates'
        }
    }

    const isDisabled = () => {
        return range[0] !== null && range[1] !== null && dayjs(range[0]).isAfter(range[1]);
    }

    return (
        <>
            <Button
                disabled={disabled}
                onClick={handleOpen}
                endIcon={<ExpandMore/>}
                variant="outlined"
            >
                {getLabel()}
            </Button>
            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                style={{width: '35%'}}
            >
                <Grid container justifyContent="center" alignItems="center">
                    <Grid container size={10}>
                        <Grid container style={{margin: '1em 0'}} size={12}>
                            <Grid size={5}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label={'Start year'}
                                        views={['year']}
                                        value={range[0]}
                                        onChange={(date) => {
                                            if (date) {
                                                date = dayjs({year: date.year(), month: 0, day: 1} as DayJsObject)
                                                console.log('from date', date.format('YYYY-MM-DD'))
                                                setRange(r => ([date, r[1]]))
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={1}></Grid>
                            <Grid size={4}>
                                <ToggleButtonGroup
                                    color="primary"
                                    exclusive
                                    aria-label="Platform"
                                    fullWidth={true}
                                    value={range[0] ? String(Math.floor(range[0].month() / 3)) : ''}
                                    onChange={(e, value) => {
                                        onClickQuarter(false, Number(value))
                                    }}
                                    style={{height: '100%'}}
                                >
                                    <ToggleButton value="0">Q1</ToggleButton>
                                    <ToggleButton value="1">Q2</ToggleButton>
                                    <ToggleButton value="2">Q3</ToggleButton>
                                    <ToggleButton value="3">Q4</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>
                            <Grid size={1}></Grid>
                            <Grid size={1}>
                                <Button
                                    variant={'outlined'}
                                    style={{width: '85%', height: '100%'}}
                                    onClick={() => {
                                        setRange(r => ([null, r[1]]))
                                    }}
                                >
                                    <Clear/>
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid container justifyContent={'space-evenly'} size={12}>
                            <Grid size={5}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label={'End year'}
                                        views={['year']}
                                        value={range[1]}
                                        onChange={(date) => {
                                            if (date) {
                                                date = dayjs(({year: date.year(), month: 9, day: 1} as DayJsObject))
                                                console.log('date', date.format('YYYY-MM-DD'))
                                                setRange(r => ([r[0], date]))
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid size={1}></Grid>


                            <Grid size={4}>
                                <ToggleButtonGroup
                                    color="primary"
                                    exclusive
                                    aria-label="Platform"
                                    fullWidth={true}
                                    value={range[1] ? String(Math.floor(range[1].month() / 3)) : ''}
                                    onChange={(e, value) => {
                                        onClickQuarter(true, Number(value))
                                    }}
                                    style={{height: '100%'}}
                                >
                                    <ToggleButton value="0">Q1</ToggleButton>
                                    <ToggleButton value="1">Q2</ToggleButton>
                                    <ToggleButton value="2">Q3</ToggleButton>
                                    <ToggleButton value="3">Q4</ToggleButton>
                                </ToggleButtonGroup>
                            </Grid>

                            <Grid size={1}></Grid>

                            <Grid size={1}>
                                <Button
                                    variant={'outlined'}
                                    style={{width: '85%', height: '100%'}}
                                    onClick={() => {
                                        setRange(r => ([r[0], null]))
                                    }}
                                >
                                    <Clear/>
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container sx={{m: 3}} justifyContent="end">
                    <Grid>
                        <Button
                            variant="contained"
                            disabled={isDisabled()}
                            onClick={() => {
                                setIsOpen(false);
                                onChange(range)
                            }}
                        >
                            Update
                        </Button>
                    </Grid>
                </Grid>
            </Popover>
        </>
    );
};
